import React from 'react';
import { AlertTriangle, Phone, Mail, MessageCircle, MapPin, Clock, RefreshCw, LogOut } from 'lucide-react';

/**
 * Full-page "the system is temporarily down" screen for the PMS.
 * Shown to every staff role while a super admin has the PMS paused; super
 * admins keep normal access and never see this. All copy and the contact
 * details come from `/api/system/status`.
 */

const FALLBACK_CONTACT = {
  organisation: 'Osho Himalayas',
  phone: '+91 7071 042042',
  whatsapp: 'https://wa.me/+917071042042',
  email: 'info@oshohimalayas.com',
  supportEmail: 'info@oshohimalayas.com',
  address: 'Osho Himalayas, Himachal Pradesh, India',
  website: 'https://www.oshohimalayas.com',
  hours: 'Every day, 9:00 AM – 7:00 PM IST',
};

const formatDateTime = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'Asia/Kolkata',
    });
  } catch {
    return null;
  }
};

function ContactRow({ icon: Icon, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '14px' }}>
      <Icon size={18} style={{ color: '#1A3E2A', flexShrink: 0, marginTop: '2px' }} />
      <div style={{ fontSize: '15px', color: '#36433B', lineHeight: '1.5' }}>{children}</div>
    </div>
  );
}

function MaintenanceScreen({ maintenance, contact, user, onRetry, onLogout, retrying = false }) {
  const info = { ...FALLBACK_CONTACT, ...(contact || maintenance?.contact || {}) };
  const title = maintenance?.title || 'The system is temporarily down';
  const message =
    maintenance?.message ||
    'The PMS has been paused by the administrator for maintenance. Please do not attempt any workarounds — no data has been lost and everything resumes exactly where it left off.';
  const expected = formatDateTime(maintenance?.expectedRestoreAt);
  const since = formatDateTime(maintenance?.since);

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#1A3E2A',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '660px',
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          boxShadow: '0 20px 48px rgba(0, 0, 0, 0.35)',
          overflow: 'hidden',
        }}
      >
        <div style={{ backgroundColor: '#772D21', padding: '24px 32px', color: '#ffffff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <AlertTriangle size={26} />
            <div>
              <div style={{ fontSize: '13px', opacity: 0.85, fontWeight: 600, letterSpacing: '0.04em' }}>
                OSHO HIMALAYAS · PMS
              </div>
              <h1 style={{ margin: '2px 0 0 0', fontSize: '22px', fontWeight: 700 }}>{title}</h1>
            </div>
          </div>
        </div>

        <div style={{ padding: '28px 32px' }}>
          <p style={{ margin: '0 0 20px 0', fontSize: '16px', lineHeight: '1.6', color: '#36433B' }}>
            {message}
          </p>

          {maintenance?.reason && (
            <div
              style={{
                padding: '12px 16px',
                backgroundColor: '#FAF4E8',
                border: '1px solid #D8B26A',
                borderRadius: '8px',
                marginBottom: '20px',
              }}
            >
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#684A1F', marginBottom: '4px' }}>
                REASON
              </div>
              <div style={{ fontSize: '14px', color: '#4F3818' }}>{maintenance.reason}</div>
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '24px' }}>
            {expected && (
              <span
                style={{
                  padding: '7px 14px',
                  backgroundColor: '#F0F3EE',
                  border: '1px solid #C4D4C8',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#0F2619',
                  fontWeight: 600,
                }}
              >
                Expected back: {expected} IST
              </span>
            )}
            {since && (
              <span
                style={{
                  padding: '7px 14px',
                  backgroundColor: '#EEEBE1',
                  border: '1px solid #E3DFD3',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#4A564D',
                }}
              >
                Paused since: {since} IST
              </span>
            )}
            {user?.name && (
              <span
                style={{
                  padding: '7px 14px',
                  backgroundColor: '#F0F3EE',
                  border: '1px solid #C4D4C8',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#0F2619',
                }}
              >
                Signed in as {user.name} ({user.role})
              </span>
            )}
          </div>

          <div style={{ height: '1px', backgroundColor: '#E3DFD3', marginBottom: '24px' }} />

          <h2 style={{ margin: '0 0 18px 0', fontSize: '17px', fontWeight: 700, color: '#1A3E2A' }}>
            You can reach us here for any queries
          </h2>

          <ContactRow icon={Phone}>
            <a href={`tel:${info.phone.replace(/\s/g, '')}`} style={{ color: '#163626', textDecoration: 'none', fontWeight: 600 }}>
              {info.phone}
            </a>
          </ContactRow>

          <ContactRow icon={MessageCircle}>
            <a href={info.whatsapp} target="_blank" rel="noopener noreferrer" style={{ color: '#163626', textDecoration: 'none', fontWeight: 600 }}>
              WhatsApp support
            </a>
          </ContactRow>

          <ContactRow icon={Mail}>
            <a href={`mailto:${info.supportEmail}`} style={{ color: '#163626', textDecoration: 'none', fontWeight: 600 }}>
              {info.supportEmail}
            </a>
            {info.email && info.email !== info.supportEmail && (
              <>
                {' · '}
                <a href={`mailto:${info.email}`} style={{ color: '#163626', textDecoration: 'none' }}>
                  {info.email}
                </a>
              </>
            )}
          </ContactRow>

          <ContactRow icon={MapPin}>{info.address}</ContactRow>

          <ContactRow icon={Clock}>{info.hours}</ContactRow>

          <div style={{ display: 'flex', gap: '12px', marginTop: '26px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={onRetry}
              disabled={retrying}
              style={{
                flex: '1 1 200px',
                padding: '12px 20px',
                backgroundColor: retrying ? '#A5A89B' : '#122E20',
                color: '#ffffff',
                border: 'none',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: retrying ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <RefreshCw size={16} style={{ animation: retrying ? 'spin 1s linear infinite' : 'none' }} />
              {retrying ? 'Checking…' : 'Check again'}
            </button>

            {onLogout && (
              <button
                type="button"
                onClick={onLogout}
                style={{
                  flex: '0 1 150px',
                  padding: '12px 20px',
                  backgroundColor: '#EEEBE1',
                  color: '#36433B',
                  border: '1px solid #D2CDBF',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <LogOut size={16} />
                Sign out
              </button>
            )}
          </div>

          <p style={{ margin: '20px 0 0 0', fontSize: '13px', color: '#5F6B61', lineHeight: '1.6' }}>
            This page refreshes automatically. You will also receive an email as soon as service is restored.
          </p>
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default MaintenanceScreen;
